// ReportPage — eindoverzicht + PDF-download + "Afronden schouw".

import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useWizardContext } from './WizardPage.jsx';
import { clusterComplete, clusterProgress } from '../hooks/useSurvey.js';
import { api, endpoints } from '../api/client.js';

const DOCUMENTS = {
    werkplan: { label: 'Werkplan', endpoint: endpoints.werkplan },
    opnamerapport: { label: 'Opnamerapport', endpoint: endpoints.opnamerapport },
};

function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function ReportPage() {
    const { t } = useTranslation();
    const { projectId } = useParams();
    const { config, project, survey, risks, reloadRisks, reloadProject, preferred } = useWizardContext();
    const [generating, setGenerating] = useState(null); // 'werkplan:pdf' e.d.
    const [completing, setCompleting] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [incomplete, setIncomplete] = useState(null);

    const clusters = config?.clusters || [];
    const allComplete = clusters.every(c => clusterComplete(c, survey.data?.[c.id], preferred?.[c.id] || 1));
    const openRisks = risks.filter(r => r.status === 'open' && r.relevant !== false);
    const hardBlocks = openRisks.filter(r => r.severity === 'hard_block');

    const generate = async (kind, format) => {
        const doc = DOCUMENTS[kind];
        setGenerating(`${kind}:${format}`);
        setError(null);
        setMessage(null);
        setIncomplete(null);
        try {
            // Het document leest uit de server: eerst alles opslaan.
            await survey.flush();
            const res = await api.post(doc.endpoint(projectId), { pdf: format === 'pdf' });
            const url = format === 'pdf' ? res.pdf_url : res.docx_url;
            if (!url) {
                throw new Error(format === 'pdf'
                    ? `PDF-conversie mislukt${res.errors?.length ? `: ${res.errors.join('; ')}` : ''}. Probeer de DOCX.`
                    : 'Geen document ontvangen');
            }
            saveBlob(await api.getBlob(url), url.split('/').pop());
            setMessage(`${doc.label} gegenereerd. De download is gestart.`);
            setIncomplete(res.missing?.length ? res.missing : null);
        } catch (err) {
            setError(err?.message || t('common.error'));
        } finally {
            setGenerating(null);
        }
    };

    const completeSurvey = async () => {
        setCompleting(true);
        setError(null);
        setMessage(null);
        try {
            await survey.flush();
            await api.post(endpoints.complete(projectId), {});
            setMessage('Schouw afgerond.');
            await reloadProject?.();
        } catch (err) {
            setError(err?.message || t('common.error'));
            await reloadRisks();
        } finally {
            setCompleting(false);
        }
    };

    const docButton = (kind, format, text, props) => {
        const key = `${kind}:${format}`;
        return (
            <Button
                size="large"
                onClick={() => generate(kind, format)}
                disabled={Boolean(generating)}
                startIcon={generating === key ? <CircularProgress size={18} color="inherit" /> : props.icon}
                variant={props.variant}
                color={props.color}
            >
                {generating === key ? 'Bezig met genereren…' : text}
            </Button>
        );
    };

    return (
        <Box>
            <Typography variant="h1" sx={{ mb: 1 }}>{t('report.title')}</Typography>
            {project && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {project.ref || project.zbs_ref || projectId} · {project.customer_name} · {project.install_address}
                </Typography>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            {incomplete && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    In het werkplan staat op {incomplete.length === 1 ? 'één plek' : `${incomplete.length} plekken`} "nog in te vullen".
                    Vul in cluster A eerst in: {incomplete.map(m => `${m.id} ${m.label}`).join('; ')}.
                </Alert>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography variant="h4" sx={{ mb: 2 }}>Cluster-overzicht</Typography>
                        <List dense>
                            {clusters.map(cluster => {
                                const data = survey.data?.[cluster.id];
                                const variant = preferred?.[cluster.id] || 1;
                                const complete = clusterComplete(cluster, data, variant);
                                const p = clusterProgress(cluster, data, variant);
                                const risk = risks.some(r => r.cluster === cluster.id && r.status === 'open' && r.relevant !== false);
                                const Icon = risk ? ErrorIcon : (complete ? CheckCircleIcon : HourglassEmptyIcon);
                                const color = risk ? 'error' : (complete ? 'success' : 'warning');
                                return (
                                    <ListItem key={cluster.id} disableGutters>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <Icon color={color} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={cluster.title}
                                            secondary={`${p.filled} van ${p.total} ingevuld`}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography variant="h4" sx={{ mb: 2 }}>Status</Typography>
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                    label={allComplete ? 'Alle verplichte velden ingevuld' : 'Nog verplichte velden open'}
                                    color={allComplete ? 'success' : 'warning'}
                                    size="small"
                                />
                            </Stack>
                            <Typography variant="body2">
                                Open risico's: <strong>{openRisks.length}</strong>
                            </Typography>
                            <Typography variant="body2">
                                Waarvan blokkerend: <strong>{hardBlocks.length}</strong>
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h4" sx={{ mb: 0.5, mt: 1 }}>Opnamerapport (intern)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Alle antwoorden, openstaande punten, risico's en foto's van deze vooropname.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                {docButton('opnamerapport', 'pdf', 'Opnamerapport PDF', { icon: <PictureAsPdfIcon />, variant: 'contained', color: 'primary' })}
                {docButton('opnamerapport', 'docx', 'Opnamerapport DOCX', { icon: <DescriptionIcon />, variant: 'outlined' })}
            </Stack>

            <Typography variant="h4" sx={{ mb: 0.5 }}>Werkplan (voor de klant)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Werkplan energieopslagsysteem in ZBS-huisstijl.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                {docButton('werkplan', 'pdf', 'Werkplan PDF', { icon: <ArticleIcon />, variant: 'contained', color: 'secondary' })}
                {docButton('werkplan', 'docx', 'Werkplan DOCX', { icon: <DescriptionIcon />, variant: 'outlined' })}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Button
                    variant="outlined"
                    size="large"
                    startIcon={completing ? <CircularProgress size={18} /> : <DoneAllIcon />}
                    onClick={completeSurvey}
                    disabled={completing || hardBlocks.length > 0 || project?.status === 'completed'}
                >
                    {project?.status === 'completed' ? 'Schouw is afgerond' : t('report.complete')}
                </Button>
                {!allComplete && project?.status !== 'completed' && (
                    <Typography variant="body2" color="text.secondary">
                        Let op: nog niet alle verplichte velden zijn ingevuld.
                    </Typography>
                )}
            </Stack>

            {hardBlocks.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    Er staan blokkerende risico's open. Los ze op, of accepteer ze met toelichting in het risicoregister, voordat je afrondt.
                </Alert>
            )}
        </Box>
    );
}
